/**
 * 生成列头字母（A, B, C, ... Z, AA, AB...）
 * @param index 列索引（从0开始）
 * @returns 对应的列字母标识
 */
export const getColumnLetter = (index: number): string => {
  let letter = '';
  let idx = index;
  while (idx >= 0) {
    letter = String.fromCharCode(65 + (idx % 26)) + letter;
    idx = Math.floor(idx / 26) - 1;
  }
  return letter;
};

/**
 * 排序状态类型
 */
export interface ISortState {
  column: number;
  order: 'asc' | 'desc' | null;
}

/**
 * 初始排序状态
 */
export const INITIAL_SORT_STATE: ISortState = {
  column: -1,
  order: null,
};

/**
 * 获取排序图标
 * @param sortState 排序状态
 * @param colIndex 列索引
 * @returns 排序图标字符
 */
export const getSortIcon = (sortState: ISortState, colIndex: number): string => {
  const isSorted = sortState.column === colIndex;
  if (isSorted && sortState.order === 'asc') {
    return '↑';
  }
  if (isSorted && sortState.order === 'desc') {
    return '↓';
  }
  return '◇';
};

/**
 * 获取排序样式类名
 * @param sortState 排序状态
 * @param colIndex 列索引
 * @returns 样式类名
 */
export const getSortClassName = (sortState: ISortState, colIndex: number): string => {
  const isSorted = sortState.column === colIndex;
  if (isSorted) {
    return ` header-sorted header-sorted--${sortState.order}`;
  }
  return '';
};

/**
 * 计算下一个排序状态
 * @param currentState 当前排序状态
 * @param colIndex 点击的列索引
 * @returns 新的排序状态
 */
export const getNextSortState = (currentState: ISortState, colIndex: number): ISortState => {
  let newOrder: 'asc' | 'desc' | null = null;

  if (currentState.column !== colIndex) {
    // 点击新列，默认升序
    newOrder = 'asc';
  } else if (currentState.order === 'asc') {
    // 当前升序，切换到降序
    newOrder = 'desc';
  } else {
    // 当前降序或无排序，清除排序
    newOrder = null;
  }

  return { column: colIndex, order: newOrder };
};

/**
 * 判断一行数据是否为空行（所有列的值都为空）
 */
const isEmptyRow = (row: Record<string, any>): boolean => {
  return Object.values(row).every(
    (val) => val === null || val === undefined || val === ''
  );
};

/**
 * 判断单元格值是否为空
 */
const isEmptyCell = (value: any): boolean => {
  return value === null || value === undefined || value === '';
};

/**
 * 自定义排序表格数据
 * 只对有数据的行进行排序，空行（整行为空或该列为空）始终保持在数据行之后
 * 兼容中间穿插空行的情况
 * @param data 表格数据
 * @param columnKey 排序列的 key
 * @param sortOrder 排序方向
 * @param columnType 列的数据类型
 * @returns 排序后的数据
 */
export const sortTableData = (
  data: Record<string, any>[],
  columnKey: string,
  sortOrder: 'asc' | 'desc',
  columnType?: string
): Record<string, any>[] => {
  // 将数据分为：有值行（该排序列有值）、该列空值但行不全空的行、完全空行
  const rowsWithValue: Record<string, any>[] = [];
  const rowsWithEmptySortCol: Record<string, any>[] = [];
  const emptyRows: Record<string, any>[] = [];

  for (const row of data) {
    if (isEmptyRow(row)) {
      emptyRows.push(row);
    } else if (isEmptyCell(row[columnKey])) {
      rowsWithEmptySortCol.push(row);
    } else {
      rowsWithValue.push(row);
    }
  }

  // 对有值的行进行排序
  rowsWithValue.sort((a, b) => {
    const valA = a[columnKey];
    const valB = b[columnKey];

    // 数值类型比较
    if (columnType === 'numeric' || columnType === 'percent') {
      const numA = typeof valA === 'number' ? valA : parseFloat(String(valA));
      const numB = typeof valB === 'number' ? valB : parseFloat(String(valB));
      if (!isNaN(numA) && !isNaN(numB)) {
        return sortOrder === 'asc' ? numA - numB : numB - numA;
      }
    }

    // 日期类型比较
    if (columnType === 'date') {
      const dateA = new Date(valA).getTime();
      const dateB = new Date(valB).getTime();
      if (!isNaN(dateA) && !isNaN(dateB)) {
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
    }

    // 尝试数值比较（自动检测）
    const numA = parseFloat(String(valA));
    const numB = parseFloat(String(valB));
    if (!isNaN(numA) && !isNaN(numB)) {
      return sortOrder === 'asc' ? numA - numB : numB - numA;
    }

    // 字符串比较
    const strA = String(valA);
    const strB = String(valB);
    const result = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
    return sortOrder === 'asc' ? result : -result;
  });

  // 按顺序拼接：有值的排序行 -> 该列为空但行不全空的行 -> 完全空行
  return [...rowsWithValue, ...rowsWithEmptySortCol, ...emptyRows];
};

/**
 * 生成新列配置（空白列，与新增列和新增 Sheet 保持一致）
 * @param baseId 基础ID（用于生成唯一标识）
 * @param index 序号
 * @param totalColumns 当前总列数
 * @returns 新的列配置
 */
export const createNewColumn = (
  baseId: number,
  index: number,
  // totalColumns: number
): { data: string; title: string; width: number; sortable: boolean; type: 'text' | 'numeric' | 'date'; } => ({
  data: `column${baseId}_${index}`,
  title: '', // 空标题，保持完全空白
  width: 150,
  sortable: true, // 新建列支持排序
  type: 'text',
});
